import { Injectable, inject } from '@angular/core';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { AssetInstalledProductsRequest, AssetOverviewDetailsRequest, AssetSummaryKpiRequest, MaintenanceTimelineRequest, ProductRequest, ProductsInstalledKpiRequest, UpdateAssetDetailsRequest } from '../models/asset-overview-request.model';
import { environment } from '../../../../environments/environment';
import { Pagination } from '../../../_shared/models/pagination-response.model';
import { AssetInstalledProductResponse, AssetOverviewDetailsResponse, TotalAssetsResponse } from '../models/asset-overview-response.model';
import { HttpClient } from '@angular/common/http';
import { Response, SummaryRequest } from '../../../_shared/models';
import { COMPLIANCE_DOCUMENTS_MOCK } from '../mockdata/asset-overview-details-data.mock';
import { delay } from '../../../_shared/helpers/delay.helper';
import { AssetImageResponse, AssetDetailsResponse, AssetOverviewSummaryResponse, ComplianceDocumentsResponse, MaintenanceTimelineEntryResponse, VehicleDetailsResponse } from '../models/asset-details-response.model';
import { AccountDateFilterRequest } from '../../../_shared/models/accountDateFilter.model';
import { KpiStatsResponse } from '../../../_shared/models/kpi-response.model';

@Injectable({
  providedIn: 'root'
})
export class AssetOverviewService {
  private httpClient = inject(HttpClient);

  async getTotalAssets(request: AccountDateFilterRequest, cancellation: Subject<void>): Promise<Response<TotalAssetsResponse>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/assetoverview/total-assets-summary`, request).pipe(takeUntil(cancellation))
    );
  }

  async getFleetUtilisation(request: SummaryRequest, cancellation: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/assetoverview/fleet-utilisation-summary`, request).pipe(takeUntil(cancellation))
    );
  }

  async getAssetsInMaintenance(request: SummaryRequest, cancellation: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/assetoverview/assets-in-maintenance-summary`, request).pipe(takeUntil(cancellation))
    );
  }

  async getOperationalAssets(request: SummaryRequest, cancellation: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/assetoverview/operational-assets-summary`, request).pipe(takeUntil(cancellation))
    );
  }

  async getProductsInstalled(request: ProductsInstalledKpiRequest, cancellation: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/assetoverview/products-installed-summary`, request).pipe(takeUntil(cancellation))
    );
  }

  async getAssetFuelConsumption(request: AssetSummaryKpiRequest, cancellation: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/assetoverview/fuel-consumption-summary`, request).pipe(takeUntil(cancellation))
    );
  }

  async getAssetInspectionsCompliance(request: AssetSummaryKpiRequest, cancellation: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/assetoverview/inspections-compliance-summary`, request).pipe(takeUntil(cancellation))
    );
  }

  async getAssetTotalDowntime(request: AssetSummaryKpiRequest, cancellation: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/assetoverview/total-downtime-summary`, request).pipe(takeUntil(cancellation))
    );
  }

  async getAssetInstalledProducts(request: AssetInstalledProductsRequest, cancellation: Subject<void>): Promise<Response<AssetInstalledProductResponse[]>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/assetoverview/installed-products`, request).pipe(takeUntil(cancellation))
    );
  }

  async sendProductRequest(request: ProductRequest, cancellation: Subject<void>): Promise<Response<void>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/assetoverview/installed-products/request`, request).pipe(takeUntil(cancellation))
    );
  }

  async getAssetOverviewDetails(request: AssetOverviewDetailsRequest, cancellation: Subject<void>): Promise<Response<Pagination<AssetOverviewDetailsResponse[]>>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/assetoverview/overview-details/filter`, request).pipe(takeUntil(cancellation))
    );
  }

  async getVehicleInformation(assetId: number, cancellation: Subject<void>): Promise<Response<VehicleDetailsResponse>> {
    return firstValueFrom(
      this.httpClient.get<Response<VehicleDetailsResponse>>(`${environment.apiEndpoint}/assetoverview/vehicle-information?assetId=${assetId}`).pipe(takeUntil(cancellation))
    );
  }

  async getAssetImage(assetId: number, cancellation: Subject<void>): Promise<Response<AssetImageResponse>> {
    return firstValueFrom(
      this.httpClient.get<Response<AssetImageResponse>>(`${environment.apiEndpoint}/assetoverview/asset-image?assetId=${assetId}`).pipe(takeUntil(cancellation))
    );
  }

  async uploadAssetImage(assetId: number, image: File, cancellation: Subject<void>): Promise<Response<AssetImageResponse>> {
    const formData = new FormData();
    formData.append('AssetId', assetId.toString());
    formData.append('Image', image, image.name);

    return firstValueFrom(
      this.httpClient.post<Response<AssetImageResponse>>(`${environment.apiEndpoint}/assetoverview/asset-image`, formData).pipe(takeUntil(cancellation))
    );
  }

  async getAssetOverviewSummary(assetId: number, cancellation: Subject<void>): Promise<Response<AssetOverviewSummaryResponse>> {
    return firstValueFrom(
      this.httpClient.get<Response<AssetOverviewSummaryResponse>>(`${environment.apiEndpoint}/assetoverview/asset-overview?assetId=${assetId}`).pipe(takeUntil(cancellation))
    );
  }

  async saveAssetDetails(request: UpdateAssetDetailsRequest, cancellation: Subject<void>): Promise<Response<AssetDetailsResponse>> {
    return firstValueFrom(
      this.httpClient.put<Response<AssetDetailsResponse>>(`${environment.apiEndpoint}/assetoverview/asset-details`, request).pipe(takeUntil(cancellation))
    );
  }

  async getMaintenanceTimeline(request: MaintenanceTimelineRequest, cancellation: Subject<void>): Promise<Response<Pagination<MaintenanceTimelineEntryResponse[]>>> {
    return firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/assetoverview/maintenance-timeline`, request).pipe(takeUntil(cancellation))
    );
  }

  async getComplianceDocuments(assetId: number, cancellation: Subject<void>): Promise<Response<ComplianceDocumentsResponse[]>> {
    await delay(2000);

    return Promise.resolve({
      data: COMPLIANCE_DOCUMENTS_MOCK
    } as Response<ComplianceDocumentsResponse[]>);
    // return firstValueFrom(this.httpClient.get(`${environment.apiEndpoint}/assetoverview/compliance-documents?assetId=${assetId}`).pipe(takeUntil(cancellation)));
  }
}